import { useState } from 'react';

import { organizerSidebar } from '@/config/sidebarConfig';
import { getOrgAdminRequestsApi, updateOrgAdminRequestApi } from '@/lib/api/organizationsApi';
import { queryKeys } from '@/lib/query/queryKeys';
import { useAuthStore } from '@/stores/auth-store';
import { OrganizationAdminRequestStatus } from '@shared/enums/domain.enum';
import type { OrganizationAdminRequest } from '@shared/types/entities';
import { formatDateAbbreviatedFr } from '@shared/utils/date.utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle, Clock, History, Inbox, UserPlus, XCircle } from 'lucide-react';
import { toast } from 'sonner';

import DashboardLayout from '../../components/layout/DashboardLayout';
import PageHeader from '../../components/ui/PageHeader';
import QueryErrorState from '../../components/ui/QueryErrorState';

// ─── Helpers ──────────────────────────────────────────────

function requesterName(r: OrganizationAdminRequest): string {
  const req = r.requester;
  if (!req) return `#${r.requesterId}`;
  return ((req.firstName ?? '') + ' ' + (req.lastName ?? '')).trim() || req.username;
}

function requesterInitials(r: OrganizationAdminRequest): string {
  const req = r.requester;
  if (!req) return '?';
  const first = req.firstName?.[0] ?? '';
  const last = req.lastName?.[0] ?? '';
  return (first + last).toUpperCase() || req.username[0].toUpperCase();
}

// ─── Pending card ─────────────────────────────────────────

function PendingCard({
  request,
  onProcessed,
}: {
  request: OrganizationAdminRequest;
  onProcessed: (id: number) => void;
}) {
  const [rejectOpen, setRejectOpen] = useState(false);
  const [reason, setReason] = useState('');
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (payload: { status: OrganizationAdminRequestStatus; rejectedReason?: string }) =>
      updateOrgAdminRequestApi(request.id, payload),
    onSuccess: (_, vars) => {
      const accepted = vars.status === OrganizationAdminRequestStatus.ACCEPTED;
      toast.success(accepted ? 'Demande acceptée' : 'Demande refusée');
      // Suppression immédiate de la carte sans attendre le refetch
      onProcessed(request.id);
      // Invalider les deux onglets en arrière-plan
      void queryClient.invalidateQueries({ queryKey: ['organizations', 'admin-requests'] });
    },
    onError: () => toast.error('Une erreur est survenue'),
  });

  return (
    <div className='rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-shadow hover:shadow-md'>
      <div className='flex items-start gap-4'>
        {/* Avatar */}
        <div className='flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full bg-indigo-600 text-sm font-bold text-white'>
          {requesterInitials(request)}
        </div>

        {/* Infos */}
        <div className='min-w-0 flex-1'>
          <div className='flex flex-wrap items-center gap-2'>
            <span className='font-semibold text-slate-900'>{requesterName(request)}</span>
            <span className='flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-semibold text-amber-700'>
              <Clock className='h-3 w-3' />
              En attente
            </span>
          </div>
          {request.requester && <p className='mt-0.5 truncate text-sm text-slate-500'>{request.requester.email}</p>}
          <p className='mt-1 text-xs text-slate-400'>Reçu le {formatDateAbbreviatedFr(request.createdAt)}</p>
        </div>
      </div>

      {request.message && (
        <div className='mt-4 rounded-xl bg-slate-50 px-4 py-3 text-sm text-slate-600 italic'>
          &ldquo;{request.message}&rdquo;
        </div>
      )}

      {!rejectOpen ? (
        <div className='mt-4 flex flex-wrap gap-3'>
          <button
            onClick={() => mutation.mutate({ status: OrganizationAdminRequestStatus.ACCEPTED })}
            disabled={mutation.isPending}
            className='flex items-center gap-2 rounded-xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50'
          >
            <CheckCircle className='h-4 w-4' />
            {mutation.isPending && mutation.variables?.status === OrganizationAdminRequestStatus.ACCEPTED
              ? 'En cours…'
              : 'Accepter'}
          </button>
          <button
            onClick={() => setRejectOpen(true)}
            disabled={mutation.isPending}
            className='flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 px-4 py-2 text-sm font-semibold text-red-600 transition hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-50'
          >
            <XCircle className='h-4 w-4' />
            Refuser
          </button>
        </div>
      ) : (
        <div className='mt-4 space-y-3'>
          <textarea
            value={reason}
            onChange={e => setReason(e.target.value)}
            placeholder='Motif du refus (requis)…'
            rows={3}
            className='w-full resize-none rounded-xl border border-slate-200 p-3 text-sm text-slate-800 outline-none focus:ring-2 focus:ring-red-300'
          />
          <div className='flex gap-3'>
            <button
              onClick={() => {
                if (!reason.trim()) {
                  toast.error('Veuillez indiquer un motif de refus');
                  return;
                }
                mutation.mutate({
                  status: OrganizationAdminRequestStatus.REJECTED,
                  rejectedReason: reason,
                });
              }}
              disabled={mutation.isPending}
              className='flex items-center gap-2 rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-700 disabled:opacity-50'
            >
              <XCircle className='h-4 w-4' />
              {mutation.isPending ? 'En cours…' : 'Confirmer le refus'}
            </button>
            <button
              onClick={() => {
                setRejectOpen(false);
                setReason('');
              }}
              disabled={mutation.isPending}
              className='rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 disabled:opacity-50'
            >
              Annuler
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── History card ─────────────────────────────────────────

function HistoryCard({ request }: { request: OrganizationAdminRequest }) {
  const accepted = request.status === OrganizationAdminRequestStatus.ACCEPTED;

  return (
    <div className='flex items-start gap-4 rounded-2xl border border-slate-100 bg-white px-5 py-4'>
      <div
        className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full text-sm font-bold text-white ${accepted ? 'bg-emerald-500' : 'bg-slate-400'}`}
      >
        {requesterInitials(request)}
      </div>

      <div className='min-w-0 flex-1'>
        <div className='flex flex-wrap items-center gap-2'>
          <span className='font-semibold text-slate-900'>{requesterName(request)}</span>
          {accepted ? (
            <span className='flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700'>
              <CheckCircle className='h-3 w-3' />
              Accepté
            </span>
          ) : (
            <span className='flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-600'>
              <XCircle className='h-3 w-3' />
              Refusé
            </span>
          )}
        </div>
        {request.requester && <p className='mt-0.5 truncate text-sm text-slate-500'>{request.requester.email}</p>}
        <p className='mt-1 text-xs text-slate-400'>
          {request.processedAt
            ? `Traité le ${formatDateAbbreviatedFr(request.processedAt)}`
            : `Reçu le ${formatDateAbbreviatedFr(request.createdAt)}`}
        </p>
        {!accepted && request.rejectedReason && (
          <p className='mt-2 rounded-lg bg-red-50 px-3 py-2 text-xs text-red-600 italic'>
            &ldquo;{request.rejectedReason}&rdquo;
          </p>
        )}
      </div>
    </div>
  );
}

// ─── Page ─────────────────────────────────────────────────

type Tab = 'pending' | 'history';

export default function OrganizerAdminRequests() {
  const isAuthenticated = useAuthStore(s => s.isAuthenticated);
  const orgId = useAuthStore(s => s.user?.organizationsAdmin[0]?.organizationId);
  const [tab, setTab] = useState<Tab>('pending');
  // IDs traités localement — suppression immédiate sans attendre le refetch
  const [processedIds, setProcessedIds] = useState<Set<number>>(new Set());

  const {
    data: pendingData,
    isLoading: pendingLoading,
    isError: pendingError,
    refetch: pendingRefetch,
  } = useQuery({
    queryKey: queryKeys.organizations.adminRequests({
      organizationId: orgId,
      status: OrganizationAdminRequestStatus.PENDING,
    } as Record<string, unknown>),
    queryFn: () =>
      getOrgAdminRequestsApi({ organizationId: orgId, status: OrganizationAdminRequestStatus.PENDING, limit: 50 }),
    enabled: !!orgId && isAuthenticated,
  });

  const {
    data: historyData,
    isLoading: historyLoading,
    isError: historyError,
    refetch: historyRefetch,
  } = useQuery({
    queryKey: queryKeys.organizations.adminRequests({
      organizationId: orgId,
      history: true,
    } as Record<string, unknown>),
    queryFn: () => getOrgAdminRequestsApi({ organizationId: orgId, limit: 50 }),
    enabled: !!orgId && isAuthenticated && tab === 'history',
  });

  const pendingRequests = (pendingData?.data ?? []).filter(r => !processedIds.has(r.id));
  const historyRequests = (historyData?.data ?? []).filter(r => r.status !== OrganizationAdminRequestStatus.PENDING);

  function handleProcessed(id: number) {
    setProcessedIds(prev => new Set(prev).add(id));
  }

  const pendingCount = pendingRequests.length;

  return (
    <DashboardLayout sidebarConfig={organizerSidebar}>
      <div className='p-8'>
        <PageHeader title='Demandes admin' subtitle='Gérez les demandes de rattachement à votre organisation' />

        {/* Tabs */}
        <div className='mb-6 flex gap-1 rounded-xl border border-slate-200 bg-slate-100 p-1 w-fit'>
          <button
            onClick={() => setTab('pending')}
            className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition ${
              tab === 'pending' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <Inbox className='h-4 w-4' />
            En attente
            {pendingCount > 0 && (
              <span className='rounded-full bg-indigo-600 px-1.5 py-0.5 text-xs font-bold text-white leading-none'>
                {pendingCount}
              </span>
            )}
          </button>
          <button
            onClick={() => setTab('history')}
            className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition ${
              tab === 'history' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <History className='h-4 w-4' />
            Historique
          </button>
        </div>

        {/* Onglet En attente */}
        {tab === 'pending' && (
          <>
            {pendingLoading ? (
              <div className='space-y-4'>
                {Array.from({ length: 3 }).map((_, i) => (
                  <div key={i} className='h-32 animate-pulse rounded-2xl bg-slate-200' />
                ))}
              </div>
            ) : pendingError ? (
              <QueryErrorState onRetry={() => void pendingRefetch()} />
            ) : pendingRequests.length === 0 ? (
              <div className='flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-white py-20 text-center'>
                <UserPlus className='mb-3 h-10 w-10 text-slate-300' />
                <p className='font-semibold text-slate-500'>Aucune demande en attente</p>
                <p className='mt-1 text-sm text-slate-400'>
                  Les demandes apparaissent ici dès qu&apos;un utilisateur souhaite rejoindre votre organisation.
                </p>
              </div>
            ) : (
              <div className='space-y-4'>
                {pendingRequests.map(r => (
                  <PendingCard key={r.id} request={r} onProcessed={handleProcessed} />
                ))}
              </div>
            )}
          </>
        )}

        {/* Onglet Historique */}
        {tab === 'history' && (
          <>
            {historyLoading ? (
              <div className='space-y-3'>
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className='h-20 animate-pulse rounded-2xl bg-slate-200' />
                ))}
              </div>
            ) : historyError ? (
              <QueryErrorState onRetry={() => void historyRefetch()} />
            ) : historyRequests.length === 0 ? (
              <div className='flex flex-col items-center justify-center rounded-2xl border border-dashed border-slate-300 bg-white py-20 text-center'>
                <History className='mb-3 h-10 w-10 text-slate-300' />
                <p className='font-semibold text-slate-500'>Aucun historique</p>
                <p className='mt-1 text-sm text-slate-400'>
                  Les demandes traitées (acceptées ou refusées) apparaîtront ici.
                </p>
              </div>
            ) : (
              <div className='space-y-3'>
                {historyRequests.map(r => (
                  <HistoryCard key={r.id} request={r} />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
